import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import useAppointmentStatus from '../../hooks/useAppointmentStatus';

const AppointmentStatusIndicator = ({ candidateEmail, showLabel = true, size = 'normal' }) => {
  const { t } = useLanguage();
  const { appointmentStatus, loading } = useAppointmentStatus(candidateEmail);

  if (loading || !appointmentStatus) {
    return null;
  }

  const getStatusInfo = () => {
    const latestAppointment = appointmentStatus.latest_appointment;
    
    if (!latestAppointment) {
      return null;
    }

    switch (latestAppointment.status) {
      case 'confirmed':
        return {
          icon: CheckCircleIcon,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          label: t('appointments.status.confirmed'),
          emoji: '❌'
        };
      default:
        return null;
    }
  };

  const statusInfo = getStatusInfo();
  if (!statusInfo) return null;

  const Icon = statusInfo.icon;
  const sizeClasses = size === 'small' ? 'h-4 w-4' : 'h-5 w-5';
  const paddingClasses = size === 'small' ? 'p-1' : 'p-1.5';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      className="inline-flex items-center"
    >
      {showLabel ? (
        <div className={`inline-flex items-center ${paddingClasses} rounded-full ${statusInfo.bgColor}`}>
          <Icon className={`${sizeClasses} ${statusInfo.color} mr-1`} />
          <span className={`text-xs font-medium ${statusInfo.color}`}>
            {statusInfo.label}
          </span>
          {appointmentStatus.latest_appointment.date && (
            <span className={`text-xs ${statusInfo.color} ml-1`}>
              ({new Date(appointmentStatus.latest_appointment.date).toLocaleDateString()})
            </span>
          )}
        </div>
      ) : (
        <div className="relative">
          <Icon 
            className={`${sizeClasses} ${statusInfo.color}`} 
            title={`${statusInfo.label} - ${appointmentStatus.latest_appointment.date}`}
          />
          {appointmentStatus.total_count > 1 && (
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-gray-600 text-white text-xs rounded-full flex items-center justify-center">
              {appointmentStatus.total_count}
            </span>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default AppointmentStatusIndicator;
